import { useState, useEffect } from 'react'
import { Plus, Trash2, ExternalLink, RefreshCw, Image as ImageIcon, Download, Search, CheckSquare, Square, Folder, Grid, Eye } from 'lucide-react'
import api from '../services/api'

interface HubService {
  id: string
  name: string
  url: string
  icon: string
  description: string
  category: string
  created_at: string
}

interface CapturedImage {
  name: string
  path: string
  size_kb: number
  modified_at: number
  folder: string
  source_domain?: string
  source_url?: string
  note_title?: string
  tags?: string[]
  week_number?: string
  week_id?: string
  date_formatted?: string
  month_year?: string
  group?: string
  region?: string
  venue?: string
}

interface ServiceStatus {
  [key: string]: 'up' | 'down' | 'checking'
}

const CATEGORIES = ["Automation", "IA", "Data", "Outils", "Outils Dev"]

export default function HubPage() {
  const [services, setServices] = useState<HubService[]>([])
  const [statuses, setStatuses] = useState<ServiceStatus>({})
  const [showForm, setShowForm] = useState(false)
  const [loading, setLoading] = useState(true)
  const [formData, setFormData] = useState({ name: '', url: '', icon: '', description: '', category: 'Automation' })
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editFormData, setEditFormData] = useState({ name: '', url: '', icon: '', description: '', category: 'Automation' })
  const [customCategory, setCustomCategory] = useState('')
  const [showCustomCategory, setShowCustomCategory] = useState(false)

  // Load services on mount
  useEffect(() => {
    fetchServices()
  }, [])

  // Auto-check status on load, then every 30s
  useEffect(() => {
    if (services.length > 0) {
      checkAllStatus()
      const interval = setInterval(checkAllStatus, 30000)
      return () => clearInterval(interval)
    }
  }, [services])

  const fetchServices = async () => {
    try {
      setLoading(true)
      const response = await api.get('/hub-services/')
      setServices(response.data)
      // Initialize status as "checking"
      const newStatuses: ServiceStatus = {}
      response.data.forEach((s: HubService) => {
        newStatuses[s.id] = 'checking'
      })
      setStatuses(newStatuses)
    } catch (err) {
      console.error('Erreur chargement services:', err)
    } finally {
      setLoading(false)
    }
  }

  const checkAllStatus = async () => {
    services.forEach(service => {
      checkStatus(service.id, service.url)
    })
  }

  const checkStatus = async (serviceId: string, url: string) => {
    try {
      const response = await api.get(`/hub-services/ping?url=${encodeURIComponent(url)}`)
      setStatuses(prev => ({
        ...prev,
        [serviceId]: response.data.status === 'up' ? 'up' : 'down'
      }))
    } catch {
      setStatuses(prev => ({
        ...prev,
        [serviceId]: 'down'
      }))
    }
  }

  const handleAddService = async () => {
    if (!formData.name.trim() || !formData.url.trim()) {
      alert('Nom et URL obligatoires')
      return
    }

    try {
      const response = await api.post('/hub-services/', {
        name: formData.name,
        url: formData.url,
        icon: formData.icon || '🔗',
        description: formData.description,
        category: formData.category
      })
      setServices([...services, response.data])
      setFormData({ name: '', url: '', icon: '', description: '', category: 'Automation' })
      setShowForm(false)
      // Check status for new service
      checkStatus(response.data.id, response.data.url)
    } catch (err) {
      console.error('Erreur ajout service:', err)
      alert('Erreur lors de l\'ajout du service')
    }
  }

  const handleDeleteService = async (serviceId: string) => {
    if (!confirm('Confirmer la suppression?')) return

    try {
      await api.delete(`/hub-services/${serviceId}`)
      setServices(services.filter(s => s.id !== serviceId))
      const newStatuses = { ...statuses }
      delete newStatuses[serviceId]
      setStatuses(newStatuses)
    } catch (err) {
      console.error('Erreur suppression service:', err)
      alert('Erreur lors de la suppression')
    }
  }

  const handleStartEdit = (service: HubService) => {
    setEditingId(service.id)
    setEditFormData({
      name: service.name,
      url: service.url,
      icon: service.icon,
      description: service.description,
      category: service.category
    })
  }

  const handleUpdateService = async (serviceId: string) => {
    if (!editFormData.name.trim() || !editFormData.url.trim()) {
      alert('Nom et URL obligatoires')
      return
    }

    try {
      const response = await api.put(`/hub-services/${serviceId}`, {
        name: editFormData.name,
        url: editFormData.url,
        icon: editFormData.icon || '🔗',
        description: editFormData.description,
        category: editFormData.category
      })
      setServices(services.map(s => s.id === serviceId ? response.data : s))
      setEditingId(null)
      // Re-check status for updated URL
      checkStatus(serviceId, response.data.url)
    } catch (err) {
      console.error('Erreur mise à jour service:', err)
      alert('Erreur lors de la mise à jour')
    }
  }

  const handleCancelEdit = () => {
    setEditingId(null)
    setEditFormData({ name: '', url: '', icon: '', description: '', category: 'Automation' })
  }

  const getStatusColor = (status?: 'up' | 'down' | 'checking') => {
    if (status === 'up') return '#22c55e' // green
    if (status === 'down') return '#ef4444' // red
    return '#6b7280' // gray
  }

  const getStatusText = (status?: 'up' | 'down' | 'checking') => {
    if (status === 'up') return '● En ligne'
    if (status === 'down') return '● Hors ligne'
    return '● Vérification...'
  }

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'Automation': '#3b82f6',  // blue
      'IA': '#8b5cf6',           // purple
      'Data': '#10b981',         // teal
      'Outils': '#f59e0b',        // amber
      'Outils Dev': '#ec4899'     // pink
    }
    return colors[category] || '#6b7280'
  }

  // Group services by category
  const servicesByCategory = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = services.filter(s => s.category === cat)
    return acc
  }, {} as { [key: string]: HubService[] })

  if (loading) {
    return (
      <div style={{ backgroundColor: '#111827', height: '100%', overflowY: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            display: 'inline-block',
            width: '40px',
            height: '40px',
            border: '4px solid #1f2937',
            borderTop: '4px solid #ec4899',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ color: '#9ca3af', marginTop: '16px', fontSize: '14px' }}>Chargement des services du Hub...</p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px', backgroundColor: '#111827', height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .category-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 20px;
          margin-bottom: 40px;
        }
        .service-card {
          background-color: #1f2937;
          border: 1px solid #374151;
          border-radius: 8px;
          padding: 20px;
          transition: transform 0.2s, box-shadow 0.2s, border-color 0.2s;
          display: flex;
          flex-direction: column;
        }
        .service-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
          border-color: #ec4899;
        }
      `}</style>

      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1 style={{ color: '#ffffff', fontSize: '28px', fontWeight: 'bold' }}>
            🌐 Hub Services
          </h1>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => {
                checkAllStatus()
                setStatuses(services.reduce((acc, s) => ({ ...acc, [s.id]: 'checking' }), {}))
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#374151',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#4b5563'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#374151'}
            >
              <RefreshCw size={16} />
              Rafraîchir
            </button>
            <button
              onClick={() => setShowForm(!showForm)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                backgroundColor: '#ec4899',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#db2777'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ec4899'}
            >
              <Plus size={16} />
              Ajouter un service
            </button>
          </div>
        </div>

        {/* Services-only configuration form */}
        {showForm && (
          <div style={{
            backgroundColor: '#1f2937',
            border: '1px solid #374151',
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '32px'
          }}>
            <h3 style={{ color: '#ffffff', marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>
              Ajouter un nouveau service
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={{ color: '#d1d5db', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Nom du service *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Mon Service"
                  style={{
                    width: '100%',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    color: '#ffffff',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ color: '#d1d5db', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Catégorie
                </label>
                {!showCustomCategory ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select
                      value={formData.category}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') {
                          setShowCustomCategory(true)
                          setFormData({ ...formData, category: '' })
                        } else {
                          setFormData({ ...formData, category: e.target.value })
                        }
                      }}
                      style={{
                        flex: 1,
                        backgroundColor: '#111827',
                        border: '1px solid #374151',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        color: '#ffffff',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    >
                      {CATEGORIES.map(cat => (
                        <option key={cat} value={cat} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
                          {cat}
                        </option>
                      ))}
                      <option value="__custom__" style={{ backgroundColor: '#1f2937', color: '#ec4899', fontWeight: 'bold' }}>
                        ➕ Nouvelle catégorie...
                      </option>
                    </select>
                  </div>
                ) : (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                      value={customCategory}
                      onChange={(e) => {
                        setCustomCategory(e.target.value)
                        setFormData({ ...formData, category: e.target.value })
                      }}
                      placeholder="Nouvelle catégorie"
                      autoFocus
                      style={{
                        flex: 1,
                        backgroundColor: '#111827',
                        border: '1px solid #ec4899',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        color: '#ffffff',
                        fontSize: '14px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <button
                      onClick={() => {
                        setShowCustomCategory(false)
                        setCustomCategory('')
                        setFormData({ ...formData, category: 'Automation' })
                      }}
                      style={{
                        padding: '8px 12px',
                        backgroundColor: '#374151',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#d1d5db',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      title="Annuler"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label style={{ color: '#d1d5db', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  URL *
                </label>
                <input
                  type="text"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="Ex: http://31.97.52.64:5678"
                  style={{
                    width: '100%',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    color: '#ffffff',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div>
                <label style={{ color: '#d1d5db', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Icône (emoji)
                </label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="Ex: ⚡"
                  maxLength={10}
                  style={{
                    width: '100%',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    color: '#ffffff',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ color: '#d1d5db', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Ex: Mon service description"
                  style={{
                    width: '100%',
                    backgroundColor: '#111827',
                    border: '1px solid #374151',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    color: '#ffffff',
                    fontSize: '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handleAddService}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#ec4899',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Ajouter
              </button>
              <button
                onClick={() => {
                  setShowForm(false)
                  setFormData({ name: '', url: '', icon: '', description: '', category: 'Automation' })
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#374151',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Annuler
              </button>
            </div>
          </div>
        )}

        {/* Services View */}
        {true && (
          <>
            {CATEGORIES.map(category => {
              const categoryServices = servicesByCategory[category]
              if (categoryServices.length === 0) return null

              return (
                <div key={category} style={{ marginBottom: '40px' }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '20px',
                    borderBottom: `3px solid ${getCategoryColor(category)}`
                  }}>
                    <h2 style={{
                      color: getCategoryColor(category),
                      fontSize: '20px',
                      fontWeight: 'bold',
                      margin: '0 0 8px 0'
                    }}>
                      {category}
                    </h2>
                    <span style={{ color: '#9ca3af', fontSize: '14px' }}>
                      ({categoryServices.length})
                    </span>
                  </div>

                  <div className="category-grid">
                    {categoryServices.map(service => (
                      <div key={service.id} className="service-card">
                        {editingId === service.id ? (
                          // Mode édition inline
                          <div>
                            <h3 style={{ color: '#ffffff', marginBottom: '16px', fontSize: '16px', fontWeight: '600' }}>
                              Modifier le service
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
                              <div>
                                <label style={{ color: '#d1d5db', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                  Nom
                                </label>
                                <input
                                  type="text"
                                  value={editFormData.name}
                                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                                  style={{
                                    width: '100%',
                                    backgroundColor: '#111827',
                                    border: '1px solid #374151',
                                    borderRadius: '6px',
                                    padding: '6px 10px',
                                    color: '#ffffff',
                                    fontSize: '13px',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              </div>
                              <div>
                                <label style={{ color: '#d1d5db', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                  Catégorie
                                </label>
                                <select
                                  value={editFormData.category}
                                  onChange={(e) => setEditFormData({ ...editFormData, category: e.target.value })}
                                  style={{
                                    width: '100%',
                                    backgroundColor: '#111827',
                                    border: '1px solid #374151',
                                    borderRadius: '6px',
                                    padding: '6px 10px',
                                    color: '#ffffff',
                                    fontSize: '13px',
                                    boxSizing: 'border-box'
                                  }}
                                >
                                  {CATEGORIES.map(cat => (
                                    <option key={cat} value={cat} style={{ backgroundColor: '#1f2937', color: '#ffffff' }}>
                                      {cat}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label style={{ color: '#d1d5db', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                  Icône
                                </label>
                                <input
                                  type="text"
                                  value={editFormData.icon}
                                  onChange={(e) => setEditFormData({ ...editFormData, icon: e.target.value })}
                                  maxLength={10}
                                  style={{
                                    width: '100%',
                                    backgroundColor: '#111827',
                                    border: '1px solid #374151',
                                    borderRadius: '6px',
                                    padding: '6px 10px',
                                    color: '#ffffff',
                                    fontSize: '13px',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              </div>
                              <div>
                                <label style={{ color: '#d1d5db', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                  URL
                                </label>
                                <input
                                  type="text"
                                  value={editFormData.url}
                                  onChange={(e) => setEditFormData({ ...editFormData, url: e.target.value })}
                                  style={{
                                    width: '100%',
                                    backgroundColor: '#111827',
                                    border: '1px solid #374151',
                                    borderRadius: '6px',
                                    padding: '6px 10px',
                                    color: '#ffffff',
                                    fontSize: '13px',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              </div>
                              <div style={{ gridColumn: '1 / -1' }}>
                                <label style={{ color: '#d1d5db', fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                                  Description
                                </label>
                                <input
                                  type="text"
                                  value={editFormData.description}
                                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                                  style={{
                                    width: '100%',
                                    backgroundColor: '#111827',
                                    border: '1px solid #374151',
                                    borderRadius: '6px',
                                    padding: '6px 10px',
                                    color: '#ffffff',
                                    fontSize: '13px',
                                    boxSizing: 'border-box'
                                  }}
                                />
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => handleUpdateService(service.id)}
                                style={{
                                  flex: 1,
                                  padding: '6px 12px',
                                  backgroundColor: '#ec4899',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '500'
                                }}
                              >
                                Enregistrer
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                style={{
                                  flex: 1,
                                  padding: '6px 12px',
                                  backgroundColor: '#374151',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  fontWeight: '500'
                                }}
                              >
                                Annuler
                              </button>
                            </div>
                          </div>
                        ) : (
                          // Mode affichage normal
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                              <div>
                                <div style={{ fontSize: '28px', marginBottom: '8px' }}>
                                  {service.icon}
                                </div>
                                <h3 style={{ color: '#ffffff', fontSize: '18px', fontWeight: '600', margin: '0' }}>
                                  {service.name}
                                </h3>
                              </div>
                              <div style={{ display: 'flex', gap: '6px' }}>
                                <button
                                  onClick={() => handleStartEdit(service)}
                                  style={{
                                    padding: '6px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#60a5fa',
                                    transition: 'color 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.color = '#3b82f6'}
                                  onMouseLeave={(e) => e.currentTarget.style.color = '#60a5fa'}
                                  title="Modifier"
                                >
                                  ✏️
                                </button>
                                <button
                                  onClick={() => handleDeleteService(service.id)}
                                  style={{
                                    padding: '6px',
                                    backgroundColor: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#ef4444',
                                    transition: 'color 0.2s'
                                  }}
                                  onMouseEnter={(e) => e.currentTarget.style.color = '#dc2626'}
                                  onMouseLeave={(e) => e.currentTarget.style.color = '#ef4444'}
                                  title="Supprimer"
                                >
                                  <Trash2 size={18} />
                                </button>
                              </div>
                            </div>

                            {service.description && (
                              <p style={{ color: '#9ca3af', fontSize: '13px', marginBottom: '12px', margin: '0 0 12px 0' }}>
                                {service.description}
                              </p>
                            )}

                            <div style={{ marginBottom: '12px' }}>
                              <div style={{
                                color: getStatusColor(statuses[service.id]),
                                fontSize: '13px',
                                fontWeight: '600',
                                marginBottom: '8px'
                              }}>
                                {getStatusText(statuses[service.id])}
                              </div>
                              <p style={{ color: '#6b7280', fontSize: '12px', wordBreak: 'break-all', margin: '0' }}>
                                {service.url}
                              </p>
                            </div>

                            <button
                              onClick={() => window.open(service.url, '_blank')}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                backgroundColor: '#ec4899',
                                color: '#ffffff',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                transition: 'background-color 0.2s',
                                marginTop: 'auto'
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#db2777'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ec4899'}
                            >
                              <ExternalLink size={16} />
                              Ouvrir
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}

            {services.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#9ca3af' }}>
                <p>Aucun service configuré. Cliquez sur "Ajouter un service" pour commencer.</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
